import React from 'react';
import { Link } from 'react-router-dom';
import {
  Plane, ShieldCheck, Sparkles, Users, Award, Building2,
  Globe2, MessageCircle, ArrowRight, CheckCircle2,
} from 'lucide-react';
import LegalLayout from '../../components/LegalLayout';
import { COMPANY } from './companyConfig';

const TRUST_METRICS = [
  { label: 'Year Founded', value: '2002' },
  { label: 'Years of Experience', value: '23+' },
  { label: 'Airline Tickets Issued', value: '100,000+' },
  { label: 'Visas Processed', value: '100,000+' },
  { label: 'Team Members', value: '40+' },
];

const WHY_TABLE = [
  ['Trusted Since 2002', 'Over 23 years of verified industry experience'],
  ['Airline Authorization', 'Authorized Selling Agents for Iran Air and Mahan Air'],
  ['Scale', '100,000+ tickets issued, 100,000+ visas processed'],
  ['B2B Expertise', "One of India's largest Middle East travel B2B aggregators"],
  ['Modern Technology', 'OTA platform with intelligent booking infrastructure'],
  ['Transparent Pricing', 'Clear fare breakdowns with no hidden charges'],
  ['Dedicated Support', 'Operational assistance available across multiple channels'],
  ['Industry Presence', 'Active exhibitor at OTM, TTF, and ITM'],
];

const VALUES = [
  { title: 'Reliability', body: 'We deliver what we commit to, consistently.' },
  { title: 'Transparency', body: 'Honest pricing, clear communication, no surprises.' },
  { title: 'Partnership', body: 'Built for travel agents and travelers equally.' },
  { title: 'Innovation', body: 'Continuously investing in better travel technology.' },
  { title: 'Expertise', body: 'Deep knowledge of the routes, markets, and operations we serve.' },
];

const AIRLINES = [
  'Iran Air', 'Mahan Air', 'flydubai', 'Jazeera Airways', 'SalamAir', 'Oman Air',
];

const AboutUs = () => (
  <LegalLayout
    title="About Us"
    subtitle="23+ years of trusted travel expertise, combined with modern travel technology and a team built on operational excellence."
    breadcrumbLabel="Company"
    canonicalPath="/about-us"
    description="ACF Tourism — founded 2002, Mumbai. 23+ years serving India-Middle East travel with 100,000+ tickets, authorized airline partnerships, and a modern OTA platform."
    testId="about-us-page"
  >
    {/* Trust metrics strip */}
    <div className="not-prose grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
      {TRUST_METRICS.map((m) => (
        <div
          key={m.label}
          className="text-center p-4 bg-gradient-to-br from-[#F5FBFB] to-white border border-[#659A9B]/20 rounded-xl"
          data-testid={`about-metric-${m.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <div className="text-2xl md:text-3xl font-bold text-[#1B375B]">{m.value}</div>
          <div className="text-xs text-gray-600 mt-1">{m.label}</div>
        </div>
      ))}
    </div>

    <h2>Our Journey</h2>
    <h3>How We Started</h3>
    <p>
      <strong>{COMPANY.brand}</strong> was founded in 2002 from a small office on SVP Road,
      Mumbai, with a team of just three people and a clear focus — to provide reliable, honest
      travel services to Indian travelers heading to the Middle East.
    </p>
    <p>
      Over the years, that small operation grew steadily. We built a reputation for operational
      reliability, strong airline relationships, and consistent service quality. What began as
      a ticketing-focused agency evolved into one of India&rsquo;s most trusted B2B and B2C
      travel companies serving the Middle East travel market.
    </p>
    <p>
      Today, ACF Tourism operates with a dedicated team of over 40 professionals, a growing
      network of travel agent partners across India, authorized airline selling agreements,
      and an expanding digital travel platform designed for the future of the industry.
    </p>

    <h2>A Trusted Name in Middle East Travel</h2>
    <p>
      Recognized as one of India&rsquo;s largest B2B aggregators for the Middle East travel
      market, ACF Tourism has built its reputation through consistent performance, deep
      operational expertise, and a strong network of airline and travel industry relationships.
    </p>
    <p>
      We serve travel agents, corporate clients, and individual travelers across India —
      offering air ticketing, visa processing, and complete international travel solutions
      through both traditional and digital channels.
    </p>
    <p>
      Our specialization in Middle East travel routes — particularly India–Iran and India–Iraq
      corridors — gives us a depth of knowledge and operational capability that is difficult
      to match.
    </p>

    <h2>Airline Partnerships and Recognition</h2>
    <p>
      ACF Tourism is proud to be an Authorized Selling Agent for <strong>Iran Air</strong> and
      <strong> Mahan Air</strong>. For over a decade, we have consistently ranked among the
      top-performing sellers for Iran Air in the region — a reflection of our commitment to
      volume, reliability, and long-term partnership.
    </p>
    <p>
      Our service standards and operational performance have also earned us appreciation and
      recognition from leading international airlines including flydubai, Jazeera Airways,
      SalamAir, and Oman Air. These relationships are not just commercial — they represent
      years of trust built through consistent delivery.
    </p>
    <div className="not-prose flex flex-wrap gap-2 my-6">
      {AIRLINES.map((a) => (
        <span
          key={a}
          className="px-3 py-1.5 bg-[#1B375B]/5 border border-[#1B375B]/15 rounded-full text-sm font-medium text-[#1B375B]"
        >
          <Plane className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />{a}
        </span>
      ))}
    </div>

    <h2>Active in the Travel Trade Community</h2>
    <p>
      ACF Tourism has been an active exhibitor at India&rsquo;s leading travel trade
      exhibitions — including OTM (Outbound Travel Mart), TTF (Travel &amp; Tourism Fair),
      and ITM (International Travel Mart). Our consistent presence at these events reflects
      our commitment to building meaningful, long-term relationships within the global travel
      industry.
    </p>
    <p>
      These platforms allow us to connect with airline partners, travel agents, destination
      management companies, and tourism boards — and to stay closely engaged with the
      direction of the industry.
    </p>

    <h2>The Future of Travel at ACF Tourism</h2>
    <p>
      ACF Tourism has always been an operations-first company. But the future of travel is
      digital — and we are building for it.
    </p>
    <p>
      We have launched our modern Online Travel Platform, giving travelers and partners
      real-time access to flight search, booking, and travel management tools. Our technology
      stack includes automated ticketing, AI-assisted booking support, digital visa workflows,
      and scalable systems designed to handle the needs of both individual travelers and
      high-volume B2B partners.
    </p>
    <p>We are not just adapting to the future of travel. We are actively building it.</p>

    <h2>Why Travel Professionals and Travelers Choose Us</h2>
    <div className="not-prose overflow-x-auto my-6">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-[#F5FBFB] text-[#1B375B]">
            <th className="text-left px-4 py-3 border-b border-[#659A9B]/20 font-semibold">Strength</th>
            <th className="text-left px-4 py-3 border-b border-[#659A9B]/20 font-semibold">Detail</th>
          </tr>
        </thead>
        <tbody>
          {WHY_TABLE.map(([s, d]) => (
            <tr key={s} className="hover:bg-gray-50">
              <td className="px-4 py-3 border-b border-gray-100 font-medium text-[#1B375B]">{s}</td>
              <td className="px-4 py-3 border-b border-gray-100 text-gray-700">{d}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <h2>What We Stand For</h2>
    <h3>Mission</h3>
    <p>
      To deliver reliable, transparent, and technology-driven travel experiences — serving
      travelers and travel partners with the depth of 23 years of experience and the
      capability of modern travel infrastructure.
    </p>

    <h3>Values</h3>
    <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-3 my-6">
      {VALUES.map((v) => (
        <div key={v.title} className="p-4 bg-white border border-[#659A9B]/20 rounded-xl">
          <div className="flex items-center gap-2 mb-1.5">
            <CheckCircle2 className="w-4 h-4 text-[#659A9B]" />
            <span className="font-semibold text-[#1B375B]">{v.title}</span>
          </div>
          <p className="text-sm text-gray-600 m-0">{v.body}</p>
        </div>
      ))}
    </div>

    {/* Closing CTA */}
    <div className="not-prose mt-10 p-6 md:p-8 bg-gradient-to-br from-[#1B375B] to-[#335E77] text-white rounded-2xl">
      <h3 className="text-xl md:text-2xl font-bold mb-2">Get in Touch with Our Team</h3>
      <p className="text-white/85 text-sm md:text-base leading-relaxed mb-5">
        Whether you need help with flight bookings, visa processing, B2B partnership enquiries,
        or travel solutions for your business — the ACF Tourism team is ready to assist.
      </p>
      <div className="flex flex-wrap gap-3">
        <Link
          to="/contact-us"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[#1B375B] font-semibold rounded-lg hover:bg-[#E7EEEE] transition-colors"
          data-testid="about-cta-contact"
        >
          <MessageCircle className="w-4 h-4" />
          Contact Us
        </Link>
        <Link
          to="/why-acf-tourism"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/15 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/25 transition-colors"
          data-testid="about-cta-why"
        >
          Why ACF Tourism
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </LegalLayout>
);

export default AboutUs;
